import React from "react";
import { Link } from "react-router-dom";

/**
 * ColiseumRibbon — ESPN/NYSE-style ticker that scrolls across the
 * homepage with the current Top-10 sellers + their headline stat for
 * the day. Live data, factual, addictive to watch.
 *
 * Implementation note: the ticker is two copies of the data stacked
 * inside the same flex row, both animated by `animation: ribbon-scroll`.
 * That gives a true seamless loop with zero JavaScript and no clones
 * jumping when one finishes. Each item is itself a Link.
 */
export default function ColiseumRibbon({ data }) {
  const items = data?.top_10 || [];
  if (!items.length) return null;

  const statFor = (s) => {
    switch (data.category) {
      case "recruiter":
        return `🔗 +${s.verified_recruits} verified`;
      case "most_loved":
        return `⭐ ${s.avg_rating} · ${s.review_count} reviews`;
      case "velocity":
        return `📈 +${s.velocity_pct}% wk`;
      case "repeat":
        return `🔁 ${s.repeat_score} repeat buyers`;
      case "sell_through":
        return `⚡ ${s.sell_through_pct}% sell-through`;
      default:
        return `🏆 ${s.composite} pts`;
    }
  };

  // Two passes for seamless scroll.
  const reel = [...items, ...items];

  return (
    <div
      className="bg-zinc-900 border-y border-amber-500/20 overflow-hidden relative"
      data-testid="coliseum-ribbon"
    >
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-zinc-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-zinc-900 to-transparent z-10 pointer-events-none" />

      <div className="flex items-center gap-3 px-6 py-2.5">
        <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-300 text-amber-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
          Live
        </span>

        <div className="flex-1 overflow-hidden">
          <div className="flex gap-7 ribbon-scroll whitespace-nowrap will-change-transform">
            {reel.map((s, i) => (
              <Link
                key={`${s.user_id}-${i}`}
                to={`/seller/${s.user_id}`}
                data-testid={`ribbon-item-${i % items.length}`}
                className="inline-flex items-center gap-2 text-xs font-mono group"
              >
                <span className="text-amber-300/70 font-black">
                  #{s.rank}
                </span>
                <span className="text-white font-semibold group-hover:text-amber-200">
                  @{s.username}
                </span>
                <span className="text-zinc-400">{statFor(s)}</span>
                {s.is_founder && (
                  <span className="text-[9px] uppercase tracking-widest text-amber-300/80 px-1.5 py-0.5 rounded-sm border border-amber-300/30">
                    Founder
                  </span>
                )}
                <span className="text-zinc-700">·</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
